import {
  SET_SUBMITTED_FORMS,
  SET_PAGINATED_FORMS,
  SET_FORM_LOADING,
  SET_RECENT_FORMS,
  GET_PORTAL_USERS,
  PORTAL_USER_LOADING,
  FORM_SUBMISSION,
  ERROR_STATES,
  GET_LOGOUT,
  SET_ACTIVE_SESSIONS,
  SET_SESSION_STATS,
  SET_SESSION_LOADING,
  SET_SECURITY_METRICS,
  SET_SECURITY_LOADING,
} from './actionTypes';

/**
 * Extracts pagination metadata from API response
 * Handles both nested page structure (e.g., mileagePage) and flat structure
 * @param {Object} data - API response data
 * @param {Object} payload - Action payload with optional pagination overrides
 * @param {Object} state - Current state for fallback values
 * @returns {Object} Normalized pagination state
 */
const extractPaginationState = (data, payload, state) => {
  const keys = Object.keys(data);
  let pageKey = keys.find((k) => k.endsWith('Page'));
  if (!pageKey) {
    pageKey = keys.find((k) => Array.isArray(data?.[k]?.content));
  }
  const page = pageKey ? data[pageKey] : data;

  const currentPageValue = Number(
    payload.currentPage ?? payload.requestedPage ?? page?.currentPage ?? 0
  );
  const totalPagesValue = Number(payload.totalPages ?? page?.totalPages ?? 0);
  const currentPage = Number.isFinite(currentPageValue) ? currentPageValue : 0;
  const totalPages = Number.isFinite(totalPagesValue) ? totalPagesValue : 0;
  const derivedHasNext = totalPages > 0 ? currentPage < totalPages - 1 : false;
  const derivedHasPrevious = currentPage > 0;

  const toBooleanOrFallback = (value, fallback) =>
    typeof value === 'boolean' ? value : fallback;

  return {
    currentPage,
    totalPages,
    totalElements:
      Number(payload.totalElements ?? page?.totalElements ?? 0) || 0,
    pageSize:
      Number(payload.pageSize ?? page?.pageSize ?? state.pageSize) ||
      state.pageSize,
    hasNext: toBooleanOrFallback(
      payload.hasNext ?? page?.hasNext,
      derivedHasNext
    ),
    hasPrevious: toBooleanOrFallback(
      payload.hasPrevious ?? page?.hasPrevious,
      derivedHasPrevious
    ),
  };
};

const AdminReducer = (state, action) => {
  switch (action.type) {
    case SET_SUBMITTED_FORMS:
      return {
        ...state,
        submittedForms: action.payload,
      };

    case SET_PAGINATED_FORMS: {
      const data = action.payload.data || {};
      const paginationState = extractPaginationState(
        data,
        action.payload,
        state
      );
      return {
        ...state,
        paginatedForms: data,
        ...paginationState,
      };
    }

    case SET_RECENT_FORMS:
      return {
        ...state,
        recentForms: action.payload || [],
        totalElements:
          typeof action.totalElements === 'number'
            ? action.totalElements
            : state.totalElements,
      };

    case SET_FORM_LOADING:
      return {
        ...state,
        formLoading: action.payload,
      };

    case GET_PORTAL_USERS:
      return {
        ...state,
        portalUserList: action.payload,
        userLoading: false,
      };

    case PORTAL_USER_LOADING:
      return {
        ...state,
        userLoading: action.payload,
      };

    case FORM_SUBMISSION:
      return {
        ...state,
        successMessage: action.successMessage,
        submitStatus: action.submitStatus,
        submitSuccess: action.submitSuccess,
        formLoading: action.formLoading,
      };

    case ERROR_STATES:
      return {
        ...state,
        errorMessage: action.errorMessage || '',
        userFriendlyMessage: action.userFriendlyMessage || '',
        errorReference: action.errorReference || '',
        errorCode: action.errorCode || '',
        submitStatus: action.submitStatus ?? false,
        submitFailed: action.submitFailed ?? false,
        submittedData: action.submittedData || null,
        success: action.success ?? false,
      };

    case GET_LOGOUT:
      return {
        ...state,
        auth: action.auth,
        logoutType: action.logoutType,
        logoutMsg: action.logoutMsg,
      };

    case SET_ACTIVE_SESSIONS:
      return {
        ...state,
        activeSessions: action.payload,
        sessionLoading: false,
      };

    case SET_SESSION_STATS:
      return {
        ...state,
        sessionStats: action.payload,
      };

    case SET_SESSION_LOADING:
      return {
        ...state,
        sessionLoading: action.payload,
      };

    case SET_SECURITY_METRICS:
      return {
        ...state,
        securityMetrics: action.payload,
        securityLoading: false,
      };

    case SET_SECURITY_LOADING:
      return {
        ...state,
        securityLoading: action.payload,
      };

    default:
      return state;
  }
};
export default AdminReducer;
